import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { AxiosError } from 'axios';
import { useToast } from '@common/toast';
import { FindStateType, ModalStateType } from '@components/loginModal';
import { useLogin, useUserInfo, useMutateUserInfo } from '@repository/auth/useUserInfo';
import {
  useAgreeTermsThirdPartyPrivacyProvision,
  useTermsThirdPartyPrivacyProvision,
  useTermsThirdPartyPrivacyProvisionAgreement,
} from '@repository/terms/useTerms';
import { useInsightAceessToken } from '@repository/insight/useInsight';
import { useAnalyticsJFThreeLogin } from '@repository/analytics/useAnalytics';
import LabelInput from '@components/labelInput';
import PrivacyNotice from '../privacyNotice';
import { onInputPassword } from '@utils/validators/validators';
import { getPrevPath } from '@utils/hooks/useUrlSessionStorage';
import classnames from 'classnames/bind';
import styles from './resumeLogin.module.scss';

const cx = classnames.bind(styles);

export const PROVIDE_ITEMS = '성명, 이메일, 휴대전화번호';

export interface LoginFormField {
  id: string;
  password: string;
}

interface ILoginFormProps {
  setModalState: (state: ModalStateType) => void;
  setFindState: (state: FindStateType) => void;
}

const LoginForm = ({ setModalState, setFindState }: ILoginFormProps) => {
  const Toast = useToast();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
  } = useForm<LoginFormField>();

  const {
    query: { messageUrl, jobnoticeSn, resumeDomain, companyName, jobNoticeName },
  } = router;
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoginError, setIsLoginError] = useState(false);
  const [openPrivacyNotice, setOpenPrivacyNotice] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const { data: userInfo } = useUserInfo();
  const { mutateAsync: getUserInfo } = useMutateUserInfo();
  const isLogin = !!userInfo?.id;
  const isChangePassword = userInfo?.authorities.includes('PASSWORD_CHANGE_REQUIRED');

  const { data: thirdPartyPrivacyProvisionData } = useTermsThirdPartyPrivacyProvision({
    termsType: 'INSIGHT_PRIVACY_PROVISION',
    companyName: String(companyName ?? ''),
    provideItems: PROVIDE_ITEMS,
  });
  const { mutateAsync: fetchThirdPartyPrivacyProvisionAgreement } = useTermsThirdPartyPrivacyProvisionAgreement();
  const { mutateAsync: fetchAgreeThirdPartyPrivacyProvisionAgreement } = useAgreeTermsThirdPartyPrivacyProvision();
  const { mutateAsync: fetchInsightAccessToken } = useInsightAceessToken();

  //JF3 사용자 행동 로그 분석용 api
  const { mutate: fetchAnalyticsJFThreeLogin } = useAnalyticsJFThreeLogin();

  const openJobflexResume = async () => {
    try {
      if (!thirdPartyPrivacyProvisionData) return;

      const { accessToken } = await fetchInsightAccessToken();
      //제3자제공 동의 여부 조회
      const { privacyProvisionYn } = await fetchThirdPartyPrivacyProvisionAgreement({
        termsType: 'INSIGHT_PRIVACY_PROVISION',
        companyName: `${companyName}`,
      });
      //동의 안했다면 동의 api호출
      if (!privacyProvisionYn)
        await fetchAgreeThirdPartyPrivacyProvisionAgreement({
          termsSn: thirdPartyPrivacyProvisionData.sn,
          companyName: `${companyName}`,
          provideItems: PROVIDE_ITEMS,
        });

      //JF3 사용자 행동 로그 분석용 api
      fetchAnalyticsJFThreeLogin({
        userId: userInfo?.id ?? getValues('id'),
        previousPageUrl: getPrevPath(),
        positionName: jobNoticeName?.toString(),
        companyName: companyName?.toString(),
      });

      if (messageUrl) {
        if (!window || !window.opener) return;
        window.opener.postMessage({ accessToken }, `${messageUrl}?systemKindCode=MRS2&jobnoticeSn=${jobnoticeSn}`);
      } else router.push(`${resumeDomain}?systemKindCode=MRS2&jobnoticeSn=${jobnoticeSn}&accessToken=${accessToken}`);
    } catch (e) {
      console.log(e);
    }
  };

  const handleLoginError = (error: AxiosError) => {
    setIsLoginError(true);
    if (error?.response?.data.errorCode === 'U003') {
      setFindState('PASSWORD');
      setModalState('FIND');
      Toast({
        type: 'error',
        iconType: 'info',
        content: (
          <>
            5회 이상 로그인에 실패하였습니다. <br />
            비밀번호 재설정 후 로그인 해주세요.
          </>
        ),
      });
    }
  };
  const { mutateAsync: fetchLogin } = useLogin(false, undefined, handleLoginError);

  const inputEl = useRef<HTMLInputElement>(null);

  const handlePasswordVisible = () => setPasswordVisible(!passwordVisible);

  const findIdPassword = () => {
    setFindState('ID');
    setModalState('FIND');
  };

  const onCheck = () => {
    setIsChecked(true);
    setOpenPrivacyNotice(false);
  };

  const onSubmit = async (values: LoginFormField) => {
    if (!isChecked) return;
    if (!isLogin) {
      await fetchLogin({ ...values, autoLogin: false });
      const userInfo = await getUserInfo();
      if (userInfo.authorities.includes('PASSWORD_CHANGE_REQUIRED')) {
        location.reload();
        return;
      }
    }

    if (isChangePassword) return;
    openJobflexResume();
  };

  const getFormFields = () => {
    if (!isLogin) {
      return (
        <>
          <div className={cx('infoWrap')}>
            <LabelInput
              {...register('id', { required: true })}
              type="text"
              className={cx('input', { error: errors.id || isLoginError })}
              placeholder="아이디"
              onChange={(e) => {
                setValue('id', e.target.value);
                setIsLoginError(false);
              }}
              isvalue={true}
            />
            {errors.id && <div className={cx('infoText', 'error')}>아이디를 입력해 주세요.</div>}
          </div>
          <div className={cx('infoWrap')}>
            <LabelInput
              {...register('password', { required: true })}
              type={passwordVisible ? 'text' : 'password'}
              className={cx('input', 'withButton', { error: errors.password || isLoginError })}
              placeholder="비밀번호"
              onChange={(e) => {
                setValue('password', e.target.value);
                setIsLoginError(false);
              }}
              minLength={6}
              maxLength={16}
              onInput={onInputPassword}
              isvalue={true}
            />
            {errors.password && <div className={cx('infoText', 'error')}>비밀번호를 입력해 주세요.</div>}
            <span className={cx('btnArea')}>
              <button
                type="button"
                tabIndex={-1}
                onClick={handlePasswordVisible}
                className={cx('btnConfirmPassword', passwordVisible && 'on')}
              />
            </span>
          </div>
          {isLoginError && (
            <div className={cx('infoWrap')}>
              <div className={cx('infoText', 'error')}>가입하지 않은 아이디이거나, 잘못된 비밀번호입니다.</div>
            </div>
          )}
          <div className={cx('optionArea')}>
            <button onClick={findIdPassword} className={cx('btnFindIdPassword')} type="button">
              아이디·비밀번호 찾기
            </button>
            <div className={cx('agreeArea')}>
              <label className={cx('label')}>
                <input
                  type="checkbox"
                  className={cx('input', 'hidden')}
                  ref={inputEl}
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                />
                <span className={cx('checkbox')} />
                개인정보 제3자 제공에 동의합니다. (필수)
              </label>
              <a role="button" onClick={() => setOpenPrivacyNotice(true)} className={cx('btnAgreeInfo')}>
                자세히
              </a>
            </div>
          </div>
          <button className={cx('btnApply', { isChecked })} type="submit">
            로그인
          </button>
        </>
      );
    } else {
      return (
        <>
          <div className={cx('infoWrap', { isLogin })}>
            <span className={cx('inputTitle')}>아이디</span>
            <input className={cx('input', { isLogin })} defaultValue={userInfo?.id} disabled />
          </div>
          <div className={cx('infoWrap', { isLogin })}>
            <span className={cx('inputTitle')}>이름</span>
            <input className={cx('input', { isLogin })} defaultValue={userInfo?.name} disabled />
          </div>
          <div className={cx('optionArea')}>
            <div className={cx('agreeArea')}>
              <label className={cx('label')}>
                <input
                  type="checkbox"
                  className={cx('input', 'hidden')}
                  ref={inputEl}
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                />
                <span className={cx('checkbox')} />
                개인정보 제3자 제공에 동의합니다. (필수)
              </label>
              <a role="button" onClick={() => setOpenPrivacyNotice(true)} className={cx('btnAgreeInfo')}>
                자세히
              </a>
            </div>
          </div>
          <button className={cx('btnApply', { isChecked })} type="submit">
            로그인
          </button>
        </>
      );
    }
  };

  return (
    <>
      <div className={cx('formWrap')}>
        <form onSubmit={handleSubmit(onSubmit)}>{getFormFields()}</form>
      </div>
      <PrivacyNotice
        isVisible={openPrivacyNotice}
        onClose={() => setOpenPrivacyNotice(false)}
        onCheck={onCheck}
        contents={thirdPartyPrivacyProvisionData?.contents}
      />
    </>
  );
};

export default LoginForm;
